import { Button, EmptyState, Input, Label, Modal, ModalContent, ModalDescription } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { useTopUp } from "~/common/billing/topup";
import { useAccess, whyDisabled } from "~/core/access/index";
import { ApiError, api, type RecipeCard, type TargetCard } from "~/core/api/index";
import { pick, useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { ModalHeader } from "~/core/ui/dialog";
import { Section, Stat } from "~/core/ui/page";
import { SearchSelect } from "~/core/ui/search-select";
import { Async } from "~/core/ui/states";
import { AgentEstimate } from "~/modules/analytics/components/agent-estimate";
import { type FilterState, Filters } from "~/modules/analytics/components/filters";
import { matches, TableTools } from "~/modules/analytics/components/table";
import { trackGeneration } from "~/modules/analytics/lib/generation-tracking";
import { readingPath } from "~/modules/analytics/lib/loop";
import templates from "../../../../../packages/content/writing-templates.json";

interface Props {
	entityId: string;
	filters: FilterState;
	setFilters: (next: Partial<FilterState>) => void;
	/** Where to go when this is dismissed without writing anything. */
	onClose: () => void;
}

/** Templates choose a writing angle; the existing recipe gates still decide
 * whether the brand has enough evidence to support an article. */
export function ComposeDialog({ entityId, filters, setFilters, onClose }: Props) {
	const t = useT();
	const { locale } = useI18n();
	const { editor, why } = useAccess();
	const navigate = useNavigate();
	const topUp = useTopUp();

	const [params] = useSearchParams();
	const planLocale =
		params.get("plan") && ["en", "zh"].includes(params.get("locale") ?? "")
			? (params.get("locale") as "en" | "zh")
			: null;
	const contentFilters = planLocale ? { ...filters, locale: planLocale } : filters;
	const initialTemplate = templates.find((item) => item.id === params.get("template"));
	const [templateId, setTemplateId] = useState<string | null>(initialTemplate?.id ?? null);
	const [recipe, setRecipe] = useState<string | null>(initialTemplate?.recipe ?? null);
	const [target, setTarget] = useState<string | null>(null);
	const [quality, setQuality] = useState<"standard" | "deep">("standard");
	const [query, setQuery] = useState("");
	const [busy, setBusy] = useState(false);
	const [runAt, setRunAt] = useState("");

	const state = useQuery({
		queryKey: [...keys.entity(entityId), "recipes", contentFilters.locale],
		queryFn: () => api.recipes(entityId, contentFilters),
		...read,
	});

	async function start(card: RecipeCard) {
		const deferred = params.has("plan") && runAt !== "";
		setBusy(true);
		try {
			const result = await api.startGeneration(entityId, {
				recipe: card.key,
				plan_id: params.get("plan") ?? undefined,
				run_at: deferred ? new Date(runAt).toISOString() : undefined,
				plan_action_id: params.get("action") ?? undefined,
				template_id: templateId ?? undefined,
				other_entity_id: target ?? undefined,
				locale: contentFilters.locale,
				quality,
			});
			// A deferred job would sit in the progress strip as queued until it starts.
			if (!deferred) trackGeneration(result.job_id);
			toast.success(t(deferred ? "plans.generationScheduled" : "compose.queued"));
			navigate(
				params.get("plan")
					? `${readingPath(`/w/${entityId}`, "plans")}?plan=${params.get("plan")}`
					: readingPath(`/w/${entityId}`, "drafts"),
			);
		} catch (e) {
			// The refusal is the useful part — "Coda and Notion share only 2
			// datapoints" says what to do next — so it stays until dismissed.
			toast.error(
				e instanceof ApiError ? (e.code === "bad_request" && e.detail ? e.detail : e.localized(locale)) : String(e),
				{ duration: Infinity },
			);
		} finally {
			setBusy(false);
		}
	}

	return (
		<Modal open onOpenChange={(open) => !open && onClose()}>
			<ModalContent className="compose-dialog">
				<ModalHeader>{t("compose.title")}</ModalHeader>
				<ModalDescription>{t("compose.lede")}</ModalDescription>

				{/* Outside the scroll, because the content language decides which
				    recipes can run at all and scrolling it away leaves somebody
				    reading a refusal with no sight of its cause. The language
				    alone: a range and an engine are readings of what happened,
				    and nothing here is a reading — this is what to write next. */}
				{planLocale ? (
					<p className="hint">
						{t("plans.language")}: {t(`locale.${planLocale}`)}
					</p>
				) : (
					<Filters segments={["locale"]} value={filters} onChange={setFilters} />
				)}

				<div className="compose-body">
					<Async state={state}>
						{(data) => {
							const chosen = data.recipes.find((r) => r.key === recipe) ?? null;
							const chosenTarget = data.targets.find((tg) => tg.entity_id === target) ?? null;
							const targetProblem = chosen?.needs_target ? (chosenTarget?.blocked ?? (target ? null : " ")) : null;
							// No price to check against: what a page costs is what its models
							// charge, and that is not known until they have answered. What can
							// be checked is that there is a balance to charge.
							const ready = chosen !== null && !chosen.blocked && !targetProblem && data.balance > 0;

							return (
								<>
									<div className="stats">
										<Stat label={t("compose.balance")} hint={t("hint.balance")} value={String(data.balance)} />
									</div>

									<Section title={t("compose.choose")}>
										<SearchSelect
											value={templateId}
											label={t("compose.choose")}
											placeholder={t("compose.templatePlaceholder")}
											searchLabel={t("templates.search")}
											emptyLabel={t("table.noMatch")}
											disabled={busy || !editor}
											options={templates
												.filter((template) => data.recipes.some((card) => card.key === template.recipe))
												.map((template) => {
													const card = data.recipes.find((card) => card.key === template.recipe)!;
													return {
														value: template.id,
														label: pick(locale, template.name),
														description: card.blocked
															? t(card.blocked.message_key, card.blocked.params)
															: pick(locale, template.description),
														keywords: [template.id, ...template.name, ...template.description].join(" "),
													};
												})}
											onChange={(id) => {
												const template = templates.find((template) => template.id === id);
												const card = data.recipes.find((card) => card.key === template?.recipe);
												if (!template || !card) return;
												setTemplateId(id);
												setRecipe(card.key);
												if (!card.needs_target) setTarget(null);
												if (!card.deep) setQuality("standard");
											}}
										/>
										{templateId ? (
											<p className="hint" style={{ marginTop: "var(--s-6)" }}>
												{pick(locale, templates.find((template) => template.id === templateId)?.description)}
											</p>
										) : null}
									</Section>

									{chosen?.needs_target ? (
										<Section title={t("compose.target")} lede={t("compose.targetHint")}>
											{data.targets.length === 0 ? (
												<EmptyState size="compact" title={t("compose.noTargets")} />
											) : (
												<>
													<TableTools
														query={query}
														onQuery={setQuery}
														shown={data.targets.filter((tg) => matches(query, tg.name)).length}
														total={data.targets.length}
													/>
													<div className="templates">
														{data.targets
															.filter((tg) => matches(query, tg.name))
															.map((candidate) => (
																<TargetButton
																	key={candidate.entity_id}
																	candidate={candidate}
																	active={target === candidate.entity_id}
																	onPick={() => setTarget(candidate.entity_id)}
																/>
															))}
													</div>
												</>
											)}
										</Section>
									) : null}

									{chosen ? (
										<Section title={t("compose.quality")}>
											<div className="templates">
												<QualityCard
													tier="standard"
													active={quality === "standard"}
													onPick={() => setQuality("standard")}
												/>
												{chosen.deep ? (
													<QualityCard tier="deep" active={quality === "deep"} onPick={() => setQuality("deep")} />
												) : null}
											</div>

											{params.has("plan") && (
												<fieldset disabled={!editor || busy} className="stack bare-fieldset">
													<Label htmlFor="plan-run-at">{t("plans.runAt")}</Label>
													<Input
														id="plan-run-at"
														type="datetime-local"
														value={runAt}
														onChange={(e) => setRunAt(e.target.value)}
													/>
													<p className="hint">{t("plans.runAtHint")}</p>
												</fieldset>
											)}
											<AgentEstimate entity={entityId} capabilities={["draft.generate"]} />
											<div className="row-actions" style={{ marginTop: "var(--s-7)" }}>
												<Button
													variant="primary"
													type="button"
													disabled={busy || !editor || !ready}
													title={
														chosen.blocked
															? t(chosen.blocked.message_key, chosen.blocked.params)
															: whyDisabled(editor, why.editor)
													}
													onClick={() => chosen && start(chosen)}
												>
													{t("compose.start")}
												</Button>
												{chosen.blocked ? (
													// The reason the button does nothing, where the click lands:
													// as faint text it read as a footnote and the disabled
													// button as a broken one.
													<span className="hint warn" role="status">
														{t(chosen.blocked.message_key, chosen.blocked.params)}
													</span>
												) : null}
												{data.balance <= 0 ? (
													<>
														<span className="faint">{t("gaps.noCredits")}</span>
														<Button variant="link" type="button" onClick={() => topUp("credits")}>
															{t("topup.open")}
														</Button>
													</>
												) : null}
											</div>
										</Section>
									) : null}
								</>
							);
						}}
					</Async>
				</div>
			</ModalContent>
		</Modal>
	);
}

/**
 * One candidate to compare against, with the number that decides it.
 *
 * The shared-fact count is the whole story: under three, the comparison cannot
 * be written, and saying so on the card turns a refusal after the click into a
 * choice before it.
 */
function TargetButton({ candidate, active, onPick }: { candidate: TargetCard; active: boolean; onPick: () => void }) {
	const t = useT();
	return (
		<Button
			variant="default"
			type="button"
			disabled={candidate.blocked !== null}
			aria-pressed={active}
			className={`h-auto flex-col items-start justify-start whitespace-normal p-(--s-8) text-left ${active ? "template-card active" : candidate.blocked ? "template-card off" : "template-card"}`}
			onClick={onPick}
		>
			<strong>{candidate.name}</strong>
			<span className="faint">{t("compose.shared", { n: candidate.shared })}</span>
			{candidate.blocked ? (
				<span className="template-blocked">{t(candidate.blocked.message_key, candidate.blocked.params)}</span>
			) : null}
		</Button>
	);
}

function QualityCard({ tier, active, onPick }: { tier: "standard" | "deep"; active: boolean; onPick: () => void }) {
	const t = useT();
	return (
		<Button
			variant="default"
			type="button"
			aria-pressed={active}
			className={`h-auto flex-col items-start justify-start whitespace-normal p-(--s-8) text-left ${active ? "template-card active" : "template-card"}`}
			onClick={onPick}
		>
			<strong>{t(`compose.quality.${tier}`)}</strong>
			<span className="faint">{t(`compose.quality.${tier}Why`)}</span>
		</Button>
	);
}
